import React, { useState } from 'react';
import { Button, CircularProgress } from '@mui/material';
import OrderConfirmationModal from './OrderConfirmationModal';
import OrderSuccessModal from './OrderSuccessModal';
import { placeBulkOrders } from '../../http/api';
import { useSelectedLadderOrdersStore } from '../../Store/selectedLadderOrdersStore';
import InformativeModal from './InformativeModal';
import { checkBondLadderRaceConditions } from './raceConditionCheck';
import { stagingOrderFields } from '../../Constants/stagingBlotter';
import { useAuth } from '../../Context/AuthProvider';
import { tokens } from '../../Common/designTokens';

const BondOrderProcessor = ({ 
  token, 
  selectedOrders, 
  bondLadderId, 
  ladderData, 
  onOrdersCleared, 
  onNavigateAfterSuccess,
  selectedOrdersArray,
  buttonDisabled
}) => {
  const [isPlacingOrders, setIsPlacingOrders] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isOrderProcessing, setIsOrderProcessing] = useState(false);
  const [orderError, setOrderError] = useState('');
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [isOrderSuccess, setIsOrderSuccess] = useState(false);
  const { originalSelectedOrders } = useSelectedLadderOrdersStore();
  const [infoOpen, setInfoOpen] = useState(false);
  const [infoMessage, setInfoMessage] = useState('');
  const [infoTitle, setInfoTitle] = useState('Order Update'); // NEW: dynamic modal title
  const [raceCheckLoading, setRaceCheckLoading] = useState(false); // <-- new state
  const [awaitingConfirmAfterInfo, setAwaitingConfirmAfterInfo] = useState(false); // NEW
  const { userId } = useAuth();
  // NEW: centralize generic info message
  const ORDER_LIST_MODIFIED_MSG = "The Ladder order list has been modified, please review it before submitting.";

  // REFACTORED
  const handlePlaceOrders = async () => {
    if (buttonDisabled || isPlacingOrders || raceCheckLoading) return;
    setOrderError('');
    setIsPlacingOrders(true);

    // NEW: validate for zero qtys BEFORE showing confirmation dialog
    try {
      const zeroQty = selectedOrdersArray && selectedOrdersArray.some(o => Number(o.qty) <= 0);
      if (zeroQty) {
        setIsPlacingOrders(false);
        setInfoTitle('Warning');
        setInfoMessage("Orders with 0  quantity are not allowed. Please update the quantities.");
        setInfoOpen(true);
        return;
      }
    } catch (e) {
      // continue if validation fails unexpectedly
    }

    // Non edit flow: open confirm immediately
    if (!ladderData?.fromStagingEdit) {
      setIsConfirmModalOpen(true);
      setIsPlacingOrders(false);
      return;
    }

    // Edit (restaging) flow: perform race condition check
    setRaceCheckLoading(true);
    try {
      const { warnRungs, addedRungs } = await checkBondLadderRaceConditions(token, bondLadderId);

      if (warnRungs?.length) {
        setInfoTitle('Order Update');
        setInfoMessage(ORDER_LIST_MODIFIED_MSG);
        setInfoOpen(true);
        return; // Do NOT proceed to confirmation
      }

      if (addedRungs?.length) {
        setInfoTitle('Order Update');
        setInfoMessage(ORDER_LIST_MODIFIED_MSG);
        setInfoOpen(true);
        setAwaitingConfirmAfterInfo(true); // Proceed after user closes modal
        return;
      }

      // Nothing changed -> go straight to confirmation
      setIsConfirmModalOpen(true);
    } catch (err) {
      // Fallback: still allow user to proceed
      setIsConfirmModalOpen(true);
    } finally {
      setRaceCheckLoading(false);
      setIsPlacingOrders(false);
    }
  };

  const executePlaceOrders = async () => {
    if (selectedOrdersArray.length === 0) return;

    setIsOrderProcessing(true);
    setOrderError('');
    try {
      // Use selectedOrdersArray instead of selectedOrders to get the correct calculated quantities
      const ordersToPlace = selectedOrdersArray.map((order) => {
        const origOrder = originalSelectedOrders[order.rungIndex];
        let orderId = 0;
        if (order.orderId && origOrder && origOrder.orderId && order.orderId === origOrder.orderId) {
          orderId = order.orderId;
        }
        return {
          orderId,
          bondLadderRungId: parseInt(order.rungIndex) + 1, // Rung number (1-based index)
          account: ladderData?.account || order.account,
          qty: order.qty, // This will now use the calculated qty from processedOrdersArray
          cusip: order.cusip,
          price: order.price,
          // Send the rung's originating inventory dealer as the venue. This flow used to
          // hard-code "Trading Desk", which is exactly how the inventory venue was lost.
          ...stagingOrderFields(order.brokerDealer),
          side: order.side || order.action,
          characteristicId: null,
          bondLadderId: bondLadderId,
          // pmId: ladderData?.pmId || null,
          userId: userId || null,
          // pm: ladderData?.pm || "",
          userName: ladderData?.userName || "",
          comments: order.comments || "",
          bondInventoryId: order.bondInventoryId || null
        };
      });

      // Validation: do not allow placing orders with qty <= 0
      const zeroQtyOrders = ordersToPlace.filter(o => Number(o.qty) <= 0);
      if (zeroQtyOrders.length > 0) {
        // stop processing and show informative warning to user
        setIsOrderProcessing(false);
        setIsConfirmModalOpen(false);
        setInfoTitle('Warning'); // unified exact message
        setInfoMessage("Orders with 0  quantity are not allowed. Please update the quantities.");
        setInfoOpen(true);
        return;
      }

      // Execute order placement
      const result = await placeBulkOrders(token, ordersToPlace);

      // Close confirmation dialog
      setIsConfirmModalOpen(false);
      setIsOrderProcessing(false);
      
      // Clear orders
      onOrdersCleared();
      
      // Show success modal
      setIsOrderSuccess(true);
      setIsSuccessModalOpen(true);

    } catch (error) {
      // Close confirmation dialog
      setIsConfirmModalOpen(false);
      setIsOrderProcessing(false);
      
      // Show error in success modal only (no snackbar)
      setOrderError(error.message || "Failed to place orders. Please try again.");
      setIsOrderSuccess(false);
      setIsSuccessModalOpen(true);
    }
  };
  
  const handleCancelConfirmation = () => {
    if (!isOrderProcessing) {
      setIsConfirmModalOpen(false);
      setOrderError('');
    }
  };

  const handleSuccessModalClose = () => {
    setIsSuccessModalOpen(false);
    const isAccountError = orderError && orderError.toLowerCase().includes('not active');
    if (isOrderSuccess) {
      onNavigateAfterSuccess();
    }
    if (isAccountError) {
      window.location.href = '/order?view=0&tab=2';
    }
  };

  return (
    <>
      <Button
        variant="contained"
        onClick={handlePlaceOrders}
        disabled={raceCheckLoading || isPlacingOrders || buttonDisabled}
        sx={{
          textTransform: "none",
          fontWeight: "700",
          px: "15px",
          py: "11px",
          width: ladderData?.fromStagingEdit ? "240px" : "170px",
          height: "40px",
          borderRadius: "8px",
          backgroundColor: tokens.palette.primary,
          '&:hover': { backgroundColor: tokens.palette.primary }
        }}
      >
        {ladderData?.fromStagingEdit
          ? raceCheckLoading
            ? <CircularProgress size={22} sx={{ color: tokens.colors.fontColour }} />
            : "Purchase Modified Ladder"
          : "Purchase Ladder"
        }
      </Button>

      <OrderConfirmationModal
        isConfirmOpen={isConfirmModalOpen}
        isLoading={isOrderProcessing}
        orderCount={selectedOrdersArray.length}
        onConfirm={executePlaceOrders}
        onCancel={handleCancelConfirmation}
        errorMessage={orderError}
      />

      <InformativeModal
        open={infoOpen}
        onClose={() => {
          setInfoOpen(false);
            // NEW: If this was an added-orders info, proceed to confirmation afterward
            if (awaitingConfirmAfterInfo) {
              setAwaitingConfirmAfterInfo(false);
              setIsConfirmModalOpen(true);
            }
          setInfoMessage('');
        }}
        title={infoTitle}
        message={infoMessage}
      />

      <OrderSuccessModal
        open={isSuccessModalOpen}
        onClose={handleSuccessModalClose}
        success={isOrderSuccess}
        errorMessage={orderError}
        orderCount={selectedOrdersArray.length}
      />
    </>
  );
};

export default BondOrderProcessor;
